import axiosInstance from './axiosInstance'

// ── Enums / Constants ─────────────────────────────────────────────────────────

export const CALL_TYPES = ['OUTGOING', 'INCOMING'] as const
export type CallType = typeof CALL_TYPES[number]

export const CALL_STATUSES = ['INITIATED', 'CONNECTED', 'NOT_ANSWERED', 'BUSY', 'FAILED'] as const
export type CallStatus = string

export const CALL_OUTCOMES = [
  'INTERESTED', 'NOT_INTERESTED', 'FOLLOW_UP', 'WRONG_NUMBER', 'CALLBACK', 'NO_RESPONSE',
] as const
export type CallOutcome = typeof CALL_OUTCOMES[number]

export const CALL_STATUS_LABELS: Record<string, string> = {
  INITIATED:   'Initiated',
  CONNECTED:   'Connected',
  NOT_ANSWERED:'Not Answered',
  BUSY:        'Busy',
  FAILED:      'Failed',
  ANSWERED:    'Connected',
  NO_ANSWER:   'Not Answered',
  ABANDONED:   'Not Answered',
  VOICEMAIL:   'Connected',
}

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  INTERESTED:    'Interested',
  NOT_INTERESTED:'Not Interested',
  FOLLOW_UP:     'Follow-up Required',
  WRONG_NUMBER:  'Wrong Number',
  CALLBACK:      'Callback Requested',
  NO_RESPONSE:   'No Response',
}

export const CALL_STATUS_COLORS: Record<string, string> = {
  INITIATED:   '#6b7280',
  CONNECTED:   '#16a34a',
  NOT_ANSWERED:'#f59e0b',
  BUSY:        '#f59e0b',
  FAILED:      '#dc2626',
  ANSWERED:    '#16a34a',
  NO_ANSWER:   '#f59e0b',
  ABANDONED:   '#f59e0b',
  VOICEMAIL:   '#16a34a',
  MISSED:      '#6b7280',
}

export const CALL_OUTCOME_COLORS: Record<CallOutcome, string> = {
  INTERESTED:    '#16a34a',
  NOT_INTERESTED:'#dc2626',
  FOLLOW_UP:     '#0070d2',
  WRONG_NUMBER:  '#9ca3af',
  CALLBACK:      '#7c3aed',
  NO_RESPONSE:   '#f59e0b',
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CallLog {
  id:              string
  leadId?:         string
  leadName?:       string
  leadPhone?:      string
  contactId?:      string
  contactName?:    string
  contactPhone?:   string
  dealId?:         string
  dealTitle?:      string
  salesId?:        string
  // The actual person on the deal (Deal.ContactId) — what should be shown as the
  // "contact" for a deal-only call, as opposed to the deal's title/name.
  dealContactId?:   string
  dealContactName?: string
  dealContactPhone?: string
  initiatedBy:     { id: string; name: string; email: string; avatarUrl: string | null }
  callType:        CallType
  status:          CallStatus
  outcome:         CallOutcome | null
  notes:           string | null
  followUpDate:    string | null
  startedAt:       string
  endedAt:         string | null
  durationSeconds: number | null
  recordingUrl?:   string | null
  externalCallId?: string | null
  agentCallStatus?: string | null
  customerCallStatus?: string | null
  createdAt:       string
  updatedAt:       string
  retentionCustomerId?: string
  retentionCustomerName?: string
}

export interface PaginatedCallLogs {
  items: CallLog[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface CallLogFilters {
  search?: string
  callTypes?: string[]
  statuses?: string[]
  outcomes?: string[]
  ownerIds?: string[]
  from?: string
  to?: string
  // SuperAdmin / Org Admin: narrow by org / LOB (LOB derived from the related lead/contact/deal)
  orgIds?: string[]
  lobIds?: string[]
  relatedTo?: string[]
}

export interface StartCallPayload {
  callType?:        CallType
  /** Phone number to dial — required for outbound C-Zentrix calls */
  phoneNumber?:     string
  /** The caller's C-Zentrix agent ID — comes from the logged-in user's profile */
  czentrixAgentId?: number
}

export interface EndCallPayload {
  status: CallStatus
}

export interface SaveOutcomePayload {
  status:       CallStatus
  outcome:      CallOutcome
  notes?:       string
  followUpDate?: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format seconds as "2m 34s" or "45s" */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '—'
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

/** Format call started_at as "12 Jun · 3:45 PM" */
export function formatCallTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ' · ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

export function hasValidRecordingUrl(recordingUrl?: string | null): boolean {
  if (typeof recordingUrl !== 'string') return false
  const trimmed = recordingUrl.trim()
  if (!trimmed) return false
  const normalized = trimmed.toLowerCase()
  if (normalized === 'none' || normalized === 'null' || normalized === 'undefined') return false
  try {
    const parsed = new URL(trimmed)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return trimmed.startsWith('http://') || trimmed.startsWith('https://')
  }
}

export async function downloadRecordingFile(recordingUrl?: string | null, fileName?: string): Promise<void> {
  const safeUrl = typeof recordingUrl === 'string' ? recordingUrl.trim() : ''
  if (!hasValidRecordingUrl(safeUrl)) {
    throw new Error('Invalid recording URL')
  }

  const fallbackName = fileName || `call-recording-${Date.now()}.mp3`

  // Use a direct anchor click so the browser handles the download natively.
  // Fetching via JS (fetch + blob) was hitting CORS on Knowlarity's servers and
  // returning an HTML/music-on-hold response instead of the real audio file.
  const anchor = document.createElement('a')
  anchor.href = safeUrl
  anchor.download = fallbackName
  anchor.target = '_blank'
  anchor.rel = 'noopener noreferrer'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

/** Get user-friendly explanation of call status detail (e.g. who hung up, busy, missed) */
export function getCallStatusDetailText(call: {
  status: string
  agentCallStatus?: string | null
  customerCallStatus?: string | null
}): string | null {
  const isConnected = call.status === 'CONNECTED' || call.status === 'ANSWERED'
  const agent = call.agentCallStatus?.toLowerCase()
  const customer = call.customerCallStatus?.toLowerCase()

  // If connected successfully and no disconnect/hangups, show nothing
  if (isConnected && !agent && !customer) {
    return null
  }

  // 1. Check Hangups
  if (customer === 'hanged' || customer === 'hangup') {
    return 'Call disconnected: Hanged by Customer'
  }
  if (agent === 'hanged' || agent === 'hangup') {
    return 'Call disconnected: Hanged by Agent'
  }

  // 2. Check Customer states
  if (customer === 'busy') {
    return 'Connection failed: Customer line was busy'
  }
  if (customer === 'no-answer' || customer === 'no_answer' || customer === 'missed') {
    return 'Call unanswered: Customer did not answer'
  }
  if (customer === 'failed') {
    return 'Connection failed on customer side'
  }

  // 3. Check Agent states
  if (agent === 'no-answer' || agent === 'no_answer' || agent === 'missed') {
    return 'Call unanswered: Agent did not pick up'
  }
  if (agent === 'failed') {
    return 'Connection failed on agent side'
  }

  // 4. Fallback status descriptions for unconnected calls
  if (!isConnected) {
    if (call.status === 'BUSY') return 'Connection failed: Line busy'
    if (call.status === 'NOT_ANSWERED' || call.status === 'NO_ANSWER') return 'Call unanswered: No answer'
    if (call.status === 'FAILED') return 'Connection failed'
  }

  return null
}

// ── API ───────────────────────────────────────────────────────────────────────

export const callLogsApi = {
  /** POST /leads/{leadId}/calls — initiate a call, returns the new CallLog */
  startCall: async (leadId: string, payload: StartCallPayload = {}): Promise<CallLog> => {
    const res = await axiosInstance.post<{ status: number; data: CallLog; message: string }>(
      `/leads/${leadId}/calls`,
      {
        callType:        payload.callType ?? 'OUTGOING',
        phoneNumber:     payload.phoneNumber,
        czentrixAgentId: payload.czentrixAgentId,
      }
    )
    return res.data.data
  },

  /** PATCH /leads/{leadId}/calls/{callId}/end — mark call as ended */
  endCall: async (leadId: string, callId: string, payload: EndCallPayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/leads/${leadId}/calls/${callId}/end`,
      payload
    )
    return res.data.data
  },

  /** PATCH /leads/{leadId}/calls/{callId}/outcome — submit mandatory post-call outcome */
  saveOutcome: async (leadId: string, callId: string, payload: SaveOutcomePayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/leads/${leadId}/calls/${callId}/outcome`,
      payload
    )
    return res.data.data
  },

  /** GET /leads/{leadId}/calls — all calls for a lead, newest first */
  getByLead: async (leadId: string): Promise<CallLog[]> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog[]; message: string }>(`/leads/${leadId}/calls`)
    return res.data.data
  },

  /** GET /leads/{leadId}/calls/{callId} — single call log */
  getById: async (leadId: string, callId: string): Promise<CallLog> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog; message: string }>(`/leads/${leadId}/calls/${callId}`)
    return res.data.data
  },

  /** GET /calls — all call logs for activity feed */
  getAllForActivity: async (): Promise<CallLog[]> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog[]; message: string }>('/calls')
    return res.data.data
  },

  /** GET /calls/paged — paginated call logs for activity feed */
  getPagedForActivity: async (page: number, pageSize: number, filters?: CallLogFilters): Promise<PaginatedCallLogs> => {
    const res = await axiosInstance.get<{ status: number; data: PaginatedCallLogs; message: string }>('/calls/paged', {
      params: { page, pageSize, ...filters }
    })
    return res.data.data
  },

  // ── Contact call endpoints ─────────────────────────────────────────────────
  
  /** POST /contacts/{contactId}/calls — initiate a call for a contact */
  startCallForContact: async (contactId: string, payload: StartCallPayload = {}): Promise<CallLog> => {
    const res = await axiosInstance.post<{ status: number; data: CallLog; message: string }>(
      `/contacts/${contactId}/calls`,
      {
        callType:        payload.callType ?? 'OUTGOING',
        phoneNumber:     payload.phoneNumber,
        czentrixAgentId: payload.czentrixAgentId,
      }
    )
    return res.data.data
  },

  /** PATCH /contacts/{contactId}/calls/{callId}/end — mark call as ended for contact */
  endCallForContact: async (contactId: string, callId: string, payload: EndCallPayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/contacts/${contactId}/calls/${callId}/end`,
      payload
    )
    return res.data.data
  },

  /** PATCH /contacts/{contactId}/calls/{callId}/outcome — submit post-call outcome for contact */
  saveOutcomeForContact: async (contactId: string, callId: string, payload: SaveOutcomePayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/contacts/${contactId}/calls/${callId}/outcome`,
      payload
    )
    return res.data.data
  },

  /** GET /contacts/{contactId}/calls — all calls for a contact */
  getByContact: async (contactId: string): Promise<CallLog[]> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog[]; message: string }>(`/contacts/${contactId}/calls`)
    return res.data.data
  },

  /** GET /contacts/{contactId}/calls/{callId} — single call log for contact */
  getByIdForContact: async (contactId: string, callId: string): Promise<CallLog> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog; message: string }>(`/contacts/${contactId}/calls/${callId}`)
    return res.data.data
  },

  // ── Deal call endpoints ────────────────────────────────────────────────────

  /** POST /deals/{dealId}/calls — initiate a call for a deal */
  startCallForDeal: async (dealId: string, payload: StartCallPayload = {}): Promise<CallLog> => {
    const res = await axiosInstance.post<{ status: number; data: CallLog; message: string }>(
      `/deals/${dealId}/calls`,
      {
        callType:        payload.callType ?? 'OUTGOING',
        phoneNumber:     payload.phoneNumber,
        czentrixAgentId: payload.czentrixAgentId,
      })
    return res.data.data
  },

  /** PATCH /deals/{dealId}/calls/{callId}/end — mark call as ended for deal */
  endCallForDeal: async (dealId: string, callId: string, payload: EndCallPayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/deals/${dealId}/calls/${callId}/end`, payload)
    return res.data.data
  },

  /** PATCH /deals/{dealId}/calls/{callId}/outcome — submit post-call outcome for deal */
  saveOutcomeForDeal: async (dealId: string, callId: string, payload: SaveOutcomePayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/deals/${dealId}/calls/${callId}/outcome`, payload)
    return res.data.data
  },

  // ── Retention call endpoints ───────────────────────────────────────────────

  /** POST /retention-customers/{retentionCustomerId}/calls — initiate a call for retention customer */
  startCallForRetention: async (retentionCustomerId: string, payload: StartCallPayload = {}): Promise<CallLog> => {
    const res = await axiosInstance.post<{ status: number; data: CallLog; message: string }>(
      `/retention-customers/${retentionCustomerId}/calls`,
      {
        callType:        payload.callType ?? 'OUTGOING',
        phoneNumber:     payload.phoneNumber,
        czentrixAgentId: payload.czentrixAgentId,
      })
    return res.data.data
  },

  /** PATCH /retention-customers/{retentionCustomerId}/calls/{callId}/end — mark call as ended for retention customer */
  endCallForRetention: async (retentionCustomerId: string, callId: string, payload: EndCallPayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/retention-customers/${retentionCustomerId}/calls/${callId}/end`, payload)
    return res.data.data
  },

  /** PATCH /retention-customers/{retentionCustomerId}/calls/{callId}/outcome — submit post-call outcome for retention customer */
  saveOutcomeForRetention: async (retentionCustomerId: string, callId: string, payload: SaveOutcomePayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/retention-customers/${retentionCustomerId}/calls/${callId}/outcome`, payload)
    return res.data.data
  },

  /** GET /retention-customers/{retentionCustomerId}/calls — all calls for a retention customer */
  getByRetention: async (retentionCustomerId: string): Promise<CallLog[]> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog[]; message: string }>(`/retention-customers/${retentionCustomerId}/calls`)
    return res.data.data
  },

  /** GET /deals/{dealId}/calls — all calls for a deal */
  getByDeal: async (dealId: string): Promise<CallLog[]> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog[]; message: string }>(`/deals/${dealId}/calls`)
    return res.data.data
  },

  /** GET /calls/{callId} — retrieve single call log by ID generically */
  getCallLogById: async (callId: string): Promise<CallLog> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog; message: string }>(`/calls/${callId}`)
    return res.data.data
  },

  /** GET /calls/statuses — distinct call statuses from DB */
  getStatuses: async (): Promise<string[]> => {
    const res = await axiosInstance.get<{ status: number; data: string[]; message: string }>('/calls/statuses')
    return res.data.data ?? []
  },

  // ── Sales call endpoints ───────────────────────────────────────────────────

  /** POST /sales/{salesId}/calls — initiate a call for a sales record */
  startCallForSales: async (salesId: string, payload: StartCallPayload = {}): Promise<CallLog> => {
    const res = await axiosInstance.post<{ status: number; data: CallLog; message: string }>(
      `/sales/${salesId}/calls`,
      {
        callType:        payload.callType ?? 'OUTGOING',
        phoneNumber:     payload.phoneNumber,
        czentrixAgentId: payload.czentrixAgentId,
      })
    return res.data.data
  },

  /** PATCH /sales/{salesId}/calls/{callId}/end — mark call as ended for sales */
  endCallForSales: async (salesId: string, callId: string, payload: EndCallPayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/sales/${salesId}/calls/${callId}/end`,
      payload
    )
    return res.data.data
  },

  /** PATCH /sales/{salesId}/calls/{callId}/outcome — submit post-call outcome for sales */
  saveOutcomeForSales: async (salesId: string, callId: string, payload: SaveOutcomePayload): Promise<CallLog> => {
    const res = await axiosInstance.patch<{ status: number; data: CallLog; message: string }>(
      `/sales/${salesId}/calls/${callId}/outcome`,
      payload
    )
    return res.data.data
  },

  /** GET /sales/{salesId}/calls — all calls for a sales record */
  getBySales: async (salesId: string): Promise<CallLog[]> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog[]; message: string }>(`/sales/${salesId}/calls`)
    return res.data.data
  },

  /** GET /sales/{salesId}/calls/{callId} — single call log for sales */
  getByIdForSales: async (salesId: string, callId: string): Promise<CallLog> => {
    const res = await axiosInstance.get<{ status: number; data: CallLog; message: string }>(`/sales/${salesId}/calls/${callId}`)
    return res.data.data
  }
}
